// Клиент серверных рекордов.
//
// Подключается к тем же событиям, которые игры уже слали в аналитику
// (game_start / game_over / level_complete), поэтому код самих игр про
// лидерборд ничего не знает и не менялся.

interface SubmitResponse {
  accepted: boolean
  best?: number
  rank?: number
  needs_nickname?: boolean
  top?: Array<{ rank: number; name: string; value: number; you: boolean }>
  reason?: string
}

const csrfToken = () =>
  document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''

async function api<T>(path: string, body?: Record<string, unknown>): Promise<T | null> {
  try {
    const response = await fetch(path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
    })
    return (await response.json()) as T
  } catch {
    return null // офлайн или сеть недоступна — игра продолжается как прежде
  }
}

let sessionToken: string | null = null

// Кампанийные посадки (?scene=…) открывают засеянную доску в одном-двух ходах
// от результата. Прогресс там и раньше не сохранялся — в таблицу рекордов
// такие партии тем более не идут.
const isSeededRun = () => new URLSearchParams(location.search).has('scene')

// Подписи оверлея приходят из Rails: переводы живут в config/locales,
// а не дублируются в бандле.
function label(name: string, fallback: string): string {
  return document.body.dataset[name] || fallback
}

async function startSession(game: string): Promise<void> {
  if (isSeededRun()) return

  const data = await api<{ session_token: string }>(`/api/v1/games/${game}/sessions`, {})
  sessionToken = data?.session_token ?? null
}

async function submitScore(game: string, value: number, extra: Record<string, unknown>) {
  if (sessionToken === null || isSeededRun()) return

  const data = await api<SubmitResponse>(`/api/v1/games/${game}/scores`, {
    session_token: sessionToken,
    value,
    ...extra,
  })
  sessionToken = null // сессия одноразовая и на сервере
  if (data?.accepted) showLeaderboard(data)
}

export function handleGameEvent(game: string, name: string, params: Record<string, unknown>): void {
  if (name === 'game_start') {
    void startSession(game)
    return
  }

  const value = typeof params.score === 'number' ? params.score
    : typeof params.level === 'number' ? params.level
    : null
  if (value === null) return

  if (name === 'game_over' || name === 'level_complete') {
    void submitScore(game, value, { level: params.level, result: params.result })
  }
}

// ── Оверлей ───────────────────────────────────────────────────────────────────
// Рисуется поверх canvas и живёт вне игры: у всех десяти игр свой рендер,
// вмешиваться в него ради таблицы рекордов не нужно.

function showLeaderboard(data: SubmitResponse): void {
  document.getElementById('leaderboard-overlay')?.remove()

  const overlay = document.createElement('div')
  overlay.id = 'leaderboard-overlay'
  overlay.className = 'lb-overlay'
  overlay.innerHTML = `
    <div class="lb-panel">
      <div class="lb-title">${data.rank ? `#${data.rank}` : ''} · ${data.best ?? ''}</div>
      <ol class="lb-list">
        ${(data.top ?? []).map((row) => `
          <li class="lb-row${row.you ? ' lb-row--you' : ''}">
            <span class="lb-rank">${row.rank}</span>
            <span class="lb-name"></span>
            <span class="lb-value">${row.value}</span>
          </li>`).join('')}
      </ol>
      ${data.needs_nickname ? `
        <form class="lb-form">
          <input class="lb-input" name="nickname" maxlength="16"
                 placeholder="${label('lbNamePlaceholder', 'Your name')}" required>
          <button class="lb-btn" type="submit">${label('lbSave', 'OK')}</button>
        </form>` : ''}
      <button class="lb-close" type="button">×</button>
    </div>`

  // Имена игроков подставляем текстом, а не разметкой: ник вводит человек.
  overlay.querySelectorAll('.lb-name').forEach((node, i) => {
    node.textContent = data.top?.[i]?.name ?? ''
  })

  overlay.querySelector('.lb-close')?.addEventListener('click', () => overlay.remove())
  overlay.querySelector('.lb-form')?.addEventListener('submit', async (event) => {
    event.preventDefault()
    const input = overlay.querySelector<HTMLInputElement>('.lb-input')
    if (!input?.value) return

    await fetch('/api/v1/player', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify({ nickname: input.value }),
      credentials: 'same-origin',
    })
    overlay.remove()
  })

  document.body.appendChild(overlay)
}
